import { createSelector } from '@reduxjs/toolkit';
import { List as ImList, type Map as ImMap, Set as ImSet } from 'immutable';
import { DateTime } from 'luxon';

import type { ACCOUNTS_REDUCER_KEY } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from 'app-components/flux/categories';
import type { DATE_REDUCER_KEY } from 'app-components/flux/date';
import * as dateSelectors from 'app-components/flux/date/dateSelectors';
import type { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import { type FilterObject, mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import { generatedReportsSelectors, generatedReportsUtils } from 'app-components/flux/generated-reports';
import {
  scheduledTransactionsSelectors,
  type scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import { taxDueDates } from 'app-components/flux/scheduled-transactions/scheduledTransactionsUtils';
import type { SECURITIES_REDUCER_KEY } from 'app-components/flux/securities/securitiesTypes';
import {
  type TRANSACTIONS_REDUCER_KEY,
  transactionsSelectors,
  type transactionsTypes,
  transactionsUtils,
} from 'app-components/flux/transactions';
import type { TransactionItem } from 'app-components/flux/transactions/transactionsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

import {
  mkTaxQuarter,
  type PaymentStatus,
  TAXES_REDUCER_KEY,
  type TaxPlanStats,
  type TaxQuarter,
  type TaxQuarterProps,
} from './taxesTypes';
import * as taxesUtils from './taxesUtils';

type State = Pick<
  RootState,
  | typeof TAXES_REDUCER_KEY
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
>;

export const getStore = (state: State) => state[TAXES_REDUCER_KEY];

export const getResourcesById = (state: State) => getStore(state).resourcesById;
export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;

export const getTaxPlans = createSelector(getResourcesById, (taxPlans) => {
  const taxPlansList = taxPlans.valueSeq().toList();
  return taxPlansList.sortBy((taxPlan) => taxPlan.input?.year).reverse();
});

export const getTaxPlanById = (state: State, id: string) => getResourcesById(state).get(id);

export const getTaxPlanByYear = createSelector(
  getResourcesById,
  (_state: unknown, year: number) => year,
  (taxPlans, year) => taxPlans?.find((taxPlan) => taxPlan.input?.year === year) || null,
);

export const getCurrentTaxYear = createSelector(dateSelectors.getCurrentDate, (currentDate) => {
  const previous = taxDueDates(currentDate.year - 1);
  return (currentDate?.toISODate() || '') <= previous.q4Due ? currentDate.year - 1 : currentDate.year;
});

const getTransactionPaymentStatusInternal = (
  transaction: transactionsTypes.TransactionRecord | undefined,
  scheduledTransaction: scheduledTransactionsTypes.ScheduledTransaction | undefined,
  currentDate: string,
): PaymentStatus => {
  if (!transaction) {
    return 'SKIPPED';
  } else if (currentDate) {
    if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction.state === 'CLEARED') {
      return 'PAID';
    } else if (!transaction.stDueOn) {
      return 'PENDING';
    } else if (transaction.postedOn && transaction.postedOn < currentDate) {
      return 'OVERDUE';
    } else if (transaction.stDueOn && transaction.stDueOn === currentDate) {
      return 'DUE_TODAY';
    } else if (transaction.stDueOn === scheduledTransaction?.dueOn) {
      return 'UPCOMING';
    }
  }
  return '';
};

export const getTransactionPaymentStatus = createSelector(
  (state, id: string) => transactionsSelectors.getTransactionById(state, id),
  (state, id: string) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(
      state,
      transactionsSelectors.getTransactionById(state, id)?.stModelId || '',
    ),
  dateSelectors.getCurrentDateString,
  getTransactionPaymentStatusInternal,
);

const getTaxPaymentFilter = createSelector(
  [
    (state: State, taxPlanId: string): number =>
      getTaxPlanById(state, taxPlanId)?.input?.year || dateSelectors.getCurrentDate(state).year,
    categoriesSelectors.getFederalEstimatedTaxPaymentCategories,
  ],
  (taxYear, taxPaymentCategories): FilterObject =>
    mkFilterObject({
      categories: ImSet([...taxPaymentCategories.map((category) => category.id || '').values()]),
      dateStart: DateTime.utc(taxYear, 1, 15).toISODate(),
      dateEnd: DateTime.utc(taxYear + 1, 1, 15).toISODate(),
    }),
);

const getTaxPaymentTxns = (state: State, taxPlanId: string): ImMap<string, TransactionItem> =>
  transactionsSelectors.getTransactionItemsFiltered(state, getTaxPaymentFilter(state, taxPlanId));

export const getTaxQuarters = createSelector(
  [
    getTaxPlanById,
    (state: any, taxPlanId) =>
      scheduledTransactionsSelectors.getScheduledTransactionById(
        state,
        getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId || '',
      ),
    (state: State, taxPlanId) =>
      transactionsSelectors.getSTModelTransactions(state, getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId),
    getTaxPaymentTxns,
    dateSelectors.getCurrentDate,
  ],
  (taxPlan, recurrence, transactions, taxTxns, currentDate): ImList<TaxQuarter> => {
    const dueDates = taxDueDates(taxPlan?.input?.year || currentDate.year);

    const taxQuarters: TaxQuarterProps[] = [
      {
        id: 'q1',
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q1Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q1',
        startDate: dueDates.q1Start,
        endDate: dueDates.q1End,
      },
      {
        id: 'q2',
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q2Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q2',
        startDate: dueDates.q2Start,
        endDate: dueDates.q2End,
      },
      {
        id: 'q3',
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q3Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q3',
        startDate: dueDates.q3Start,
        endDate: dueDates.q3End,
      },
      {
        id: 'q4',
        transactions: [],
        paymentStatus: '',
        dueDate: dueDates.q4Due,
        amountPaid: 0,
        amountEstimated: 0,
        label: 'Q4',
        startDate: dueDates.q4Start,
        endDate: dueDates.q4End,
      },
    ];
    if (recurrence) {
      transactions.forEach((transaction: transactionsTypes.TransactionRecord) => {
        let taxQuarter;
        if (transaction.postedOn <= dueDates.q1Due) {
          taxQuarter = taxQuarters[0];
        } else if (transaction.postedOn > dueDates.q1Due && transaction.postedOn <= dueDates.q2Due) {
          taxQuarter = taxQuarters[1];
        } else if (transaction.postedOn > dueDates.q2Due && transaction.postedOn <= dueDates.q3Due) {
          taxQuarter = taxQuarters[2];
        } else if (transaction.postedOn > dueDates.q3Due) {
          taxQuarter = taxQuarters[3];
        }
        if (taxQuarter) {
          taxQuarter.transactions.push(transaction);
          taxQuarter.amountEstimated += transaction.amount;
          if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction?.state === 'CLEARED') {
            taxQuarter.amountPaid += transaction.amount;
          }
        }
      });
    } else {
      taxTxns
        .filter((txnItem) => !transactionsUtils.isUnacceptedScheduledTxn(txnItem.transaction))
        .forEach((txnItem: transactionsTypes.TransactionItem) => {
          let taxQuarter;
          if (txnItem.transaction.postedOn <= dueDates.q1Due) {
            taxQuarter = taxQuarters[0];
          } else if (txnItem.transaction.postedOn > dueDates.q1Due && txnItem.transaction.postedOn <= dueDates.q2Due) {
            taxQuarter = taxQuarters[1];
          } else if (txnItem.transaction.postedOn > dueDates.q2Due && txnItem.transaction.postedOn <= dueDates.q3Due) {
            taxQuarter = taxQuarters[2];
          } else if (txnItem.transaction.postedOn > dueDates.q3Due) {
            taxQuarter = taxQuarters[3];
          }
          if (taxQuarter) {
            if (!taxQuarter.transactions.find((txn) => txn.id === txnItem.transaction.id)) {
              taxQuarter.transactions.push(txnItem.transaction);
            }
            taxQuarter.amountEstimated += txnItem.item.amount;
            if (
              transactionsUtils.isAcceptedScheduledTxn(txnItem.transaction) ||
              txnItem.transaction.state === 'CLEARED'
            ) {
              taxQuarter.amountPaid += txnItem.item.amount;
            }
          }
        });
    }
    return ImList(
      taxQuarters.map((taxQuarter) => {
        const transaction =
          taxQuarter.transactions.find((transaction) => !!transaction.stModelId) ||
          (taxQuarter.transactions as transactionsTypes.TransactionRecord[])[0];
        const paymentStatus: PaymentStatus = getTransactionPaymentStatusInternal(
          transaction,
          recurrence,
          currentDate.toISODate() || '',
        );
        return mkTaxQuarter({
          ...taxQuarter,
          paymentStatus,
          transactions: ImList(taxQuarter.transactions).sortBy((transaction) => transaction.postedOn),
        });
      }),
    ) as ImList<TaxQuarter>;
  },
);

export const getTaxTransactionDisplayAmount = createSelector(
  [getTaxPaymentTxns, (_state: State, _taxPlanId: string, transactionId: string) => transactionId],
  (taxTxns, transactionId) => {
    const matching = taxTxns.filter((item) => item.transaction.id === transactionId);
    if (matching.isEmpty()) return undefined;
    return matching.reduce((sum, item) => sum + item.item.amount, 0);
  },
);

export const getQuarterNow = createSelector(
  getTaxQuarters,
  dateSelectors.getCurrentDateString,
  (taxQuarters, currentDate) => {
    if (taxQuarters.size === 4) {
      const q1 = taxQuarters.get(0) as TaxQuarter;
      const q2 = taxQuarters.get(1) as TaxQuarter;
      const q3 = taxQuarters.get(2) as TaxQuarter;
      const q4 = taxQuarters.get(3) as TaxQuarter;
      if (currentDate <= q1.dueDate) {
        return q1;
      } else if (currentDate > q1.dueDate && currentDate <= q2.dueDate) {
        return q2;
      } else if (currentDate > q2.dueDate && currentDate <= q3.dueDate) {
        return q3;
      } else if (currentDate > q3.dueDate) {
        return q4;
      }
    }
    return taxQuarters.get(taxQuarters.size - 1) as TaxQuarter; // Default last quarter (if looking at prev year)
  },
);

export const getQuarterDue = createSelector(
  getTaxQuarters,
  getQuarterNow,
  (state, taxPlanId) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(
      state,
      getTaxPlanById(state, taxPlanId)?.input?.profile?.stModelId || '',
    ),
  (taxQuarters, quarterNow, scheduledTransaction) =>
    taxQuarters.find((taxQuarter: TaxQuarter) =>
      taxQuarter.transactions.find(
        (transaction: transactionsTypes.TransactionRecord) => transaction.postedOn === scheduledTransaction?.dueOn,
      ),
    ) || quarterNow,
);

export const getUpcomingQuarters = createSelector(
  [getTaxQuarters, dateSelectors.getCurrentDateString],
  (taxQuarters, currentDate): ImList<TaxQuarter> =>
    taxQuarters.filter((taxQuarter) => currentDate <= taxQuarter.endDate),
);

export const getTaxPlanStats = createSelector(getTaxPlanById, getTaxQuarters, (taxPlan, taxQuarters) =>
  taxQuarters.reduce<TaxPlanStats>(
    (stats, taxQuarter) => ({
      ...stats,
      amountPaid: stats.amountPaid + (taxQuarter.amountPaid || 0),
      amountEstimated: stats.amountEstimated + (taxQuarter.amountEstimated || 0),
      ...(!ImList.isList(taxQuarter.transactions)
        ? {}
        : taxQuarter.transactions?.reduce(
            (transactionBuckets: Partial<TaxPlanStats>, transaction: transactionsTypes.TransactionRecord) => {
              if (transactionsUtils.isAcceptedScheduledTxn(transaction) || transaction?.state === 'CLEARED') {
                transactionBuckets.transactionsPaid?.push(transaction);
              } else {
                transactionBuckets.transactionsUpcoming?.push(transaction);
              }
              return transactionBuckets;
            },
            { transactionsPaid: stats.transactionsPaid, transactionsUpcoming: stats.transactionsUpcoming },
          )),
    }),
    {
      amountPaid: taxPlan?.output?.f1040Calc?.actualAndProjectedWithholding || 0,
      amountEstimated: 0,
      transactionsPaid: [],
      transactionsUpcoming: [],
    },
  ),
);

export type BusinessStats = {
  businessIncome: number;
  businessDeductions: number;
  businessProfit: number;
};
export const businessStats = createSelector(
  [
    businessSelectors.getResourcesById,
    (state) => generatedReportsSelectors.getReport(state, { location: 'tax-planner-tax-summary' }),
  ],
  (businesses, taxPlannerTaxSummary) => {
    const results = businesses.reduce<BusinessStats>(
      (results, business) => {
        const businessRow = generatedReportsUtils.getReportRowUtil(
          taxPlannerTaxSummary,
          'BUSINESS_TYPE',
          business.id,
          '-2',
        );
        results.businessIncome +=
          generatedReportsUtils.getReportCellNumberValueUtil(
            taxPlannerTaxSummary,
            'Amount',
            'CATEGORY_TYPE',
            'INCOME',
            businessRow?.id || 'unknown',
          ) ?? 0;
        results.businessDeductions +=
          generatedReportsUtils.getReportCellNumberValueUtil(
            taxPlannerTaxSummary,
            'Amount',
            'CATEGORY_TYPE',
            'DEDUCTIBLE_EXPENSES',
            businessRow?.id || 'unknown',
          ) ?? 0;
        results.businessProfit = results.businessIncome + results.businessDeductions;
        return results;
      },
      {
        businessIncome: 0,
        businessDeductions: 0,
        businessProfit: 0,
      },
    );
    return results;
  },
);

type QuarterOverride = {
  bizIncome?: number;
  bizDeductions?: number;
};
type QuarterOverrides = {
  ytd?: QuarterOverride;
  q1?: QuarterOverride;
  q2?: QuarterOverride;
  q3?: QuarterOverride;
  q4?: QuarterOverride;
};
type QuarterTargets = {
  q1BizIncome?: number;
  q2BizIncome?: number;
  q3BizIncome?: number;
  q4BizIncome?: number;
  q1BizDeductions?: number;
  q2BizDeductions?: number;
  q3BizDeductions?: number;
  q4BizDeductions?: number;
};
const incomeKeys: Record<'q1' | 'q2' | 'q3' | 'q4', keyof QuarterTargets> = {
  q1: 'q1BizIncome',
  q2: 'q2BizIncome',
  q3: 'q3BizIncome',
  q4: 'q4BizIncome',
};
const deductionsKeys: Record<'q1' | 'q2' | 'q3' | 'q4', keyof QuarterTargets> = {
  q1: 'q1BizDeductions',
  q2: 'q2BizDeductions',
  q3: 'q3BizDeductions',
  q4: 'q4BizDeductions',
};
export const getQuarterTargets = createSelector(
  getUpcomingQuarters,
  (state, quarterOverrides: QuarterOverrides) => quarterOverrides,
  (upcomingQuarters, quarterOverrides): QuarterTargets => {
    const quarterTargets: QuarterTargets = {
      q1BizIncome: 0,
      q2BizIncome: 0,
      q3BizIncome: 0,
      q4BizIncome: 0,
      q1BizDeductions: 0,
      q2BizDeductions: 0,
      q3BizDeductions: 0,
      q4BizDeductions: 0,
    };
    upcomingQuarters.reduce(
      (target: QuarterOverride, quarter: TaxQuarter) => {
        target.bizIncome += quarterOverrides[quarter.id]?.bizIncome || 0;
        target.bizDeductions += quarterOverrides[quarter.id]?.bizDeductions || 0;
        quarterTargets[incomeKeys[quarter.id]] = target.bizIncome;
        quarterTargets[deductionsKeys[quarter.id]] = target.bizDeductions;
        return target;
      },
      {
        bizIncome: quarterOverrides.ytd.bizIncome || 0,
        bizDeductions: quarterOverrides.ytd.bizDeductions || 0,
      },
    );
    return quarterTargets;
  },
);

export const getQuarterEstimates = createSelector(
  businessStats,
  getUpcomingQuarters,
  dateSelectors.getCurrentDateString,
  getTaxPlanById,
  (businessStats, upcomingQuarters, currentDate, taxPlan) => {
    const { businessIncome, businessDeductions } = businessStats;
    const taxYear = taxPlan?.input?.year || DateTime.fromISO(currentDate).year;
    const businessIncomeEstimated = taxesUtils.estimatedAnnualAmount(taxYear, businessIncome) || 0;
    const businessDeductionsEstimated = taxesUtils.estimatedAnnualAmount(taxYear, businessDeductions) || 0;
    const businessIncomeRemaining = businessIncomeEstimated - businessIncome;
    const businessDeductionsRemaining = businessDeductionsEstimated - businessDeductions;
    const daysLeft = DateTime.fromISO(currentDate)
      .endOf('year')
      .diff(DateTime.fromISO(currentDate).endOf('day'), 'days').days;
    const quarterEstimates = upcomingQuarters.reduce(
      (quarterEstimates, quarter) => {
        const daysInQuarter = DateTime.fromISO(quarter.endDate)
          .endOf('day')
          .diff(
            currentDate > quarter.startDate
              ? DateTime.fromISO(currentDate).endOf('day')
              : DateTime.fromISO(quarter.startDate).endOf('day'),
            'days',
          ).days;
        const bizIncome = (daysInQuarter * businessIncomeRemaining) / daysLeft;
        const bizDeductions = (daysInQuarter * businessDeductionsRemaining) / daysLeft;
        quarterEstimates[quarter.id] = {
          bizIncome,
          bizDeductions,
        };
        return quarterEstimates;
      },
      {
        q1: { bizIncome: 0, bizDeductions: 0 },
        q2: { bizIncome: 0, bizDeductions: 0 },
        q3: { bizIncome: 0, bizDeductions: 0 },
        q4: { bizIncome: 0, bizDeductions: 0 },
      },
    );
    return quarterEstimates;
  },
);
export const getQuarterAdjustments = createSelector(
  [getTaxPlanById, businessStats, getUpcomingQuarters, (state, taxPlanId, quarterTargets) => quarterTargets],
  (
    taxPlan,
    businessStats,
    upcomingQuarters,
    quarterTargets: {
      q1BizIncome: number;
      q2BizIncome: number;
      q3BizIncome: number;
      q4BizIncome: number;
      q1BizDeductions: number;
      q2BizDeductions: number;
      q3BizDeductions: number;
      q4BizDeductions: number;
    },
  ) => {
    if (upcomingQuarters.isEmpty()) {
      // Handle this properly for previous years
      const q4Income =
        (quarterTargets?.q4BizIncome ?? taxPlan?.input?.profile?.q4BizIncome?.value) - businessStats.businessIncome;
      const q4Deductions =
        (quarterTargets?.q4BizDeductions ?? taxPlan?.input?.profile?.q4BizDeductions?.value) -
        businessStats.businessDeductions;
      return {
        q1: { bizIncome: 0, bizDeductions: 0 },
        q2: { bizIncome: 0, bizDeductions: 0 },
        q3: { bizIncome: 0, bizDeductions: 0 },
        q4: { bizIncome: q4Income, bizDeductions: q4Deductions },
      };
    }

    const quarterIncomeTargetsMap = {
      q1: quarterTargets?.q1BizIncome ?? taxPlan?.input?.profile?.q1BizIncome?.value,
      q2: quarterTargets?.q2BizIncome ?? taxPlan?.input?.profile?.q2BizIncome?.value,
      q3: quarterTargets?.q3BizIncome ?? taxPlan?.input?.profile?.q3BizIncome?.value,
      q4: quarterTargets?.q4BizIncome ?? taxPlan?.input?.profile?.q4BizIncome?.value,
    };
    const quarterIncomeTargets = upcomingQuarters
      .map((quarter) => quarterIncomeTargetsMap[quarter.id])
      .unshift(businessStats.businessIncome);
    const quarterDeductionsTargetsMap = {
      q1: quarterTargets?.q1BizDeductions ?? taxPlan?.input?.profile?.q1BizDeductions?.value,
      q2: quarterTargets?.q2BizDeductions ?? taxPlan?.input?.profile?.q2BizDeductions?.value,
      q3: quarterTargets?.q3BizDeductions ?? taxPlan?.input?.profile?.q3BizDeductions?.value,
      q4: quarterTargets?.q4BizDeductions ?? taxPlan?.input?.profile?.q4BizDeductions?.value,
    };
    const quarterDeductionsTargets = upcomingQuarters
      .map((quarter) => quarterDeductionsTargetsMap[quarter.id])
      .unshift(businessStats.businessDeductions);
    const quarterAdjustments = upcomingQuarters.reduce(
      (quarterAdjustments, quarter, index) => {
        const prevIncomeTarget = quarterIncomeTargets.get(index) || 0;
        const currentIncomeTarget = quarterIncomeTargets.get(index + 1) || 0;
        quarterAdjustments[quarter.id].bizIncome = currentIncomeTarget - prevIncomeTarget;
        const prevDeductionsTarget = quarterDeductionsTargets.get(index) || 0;
        const currentDeductionsTarget = quarterDeductionsTargets.get(index + 1) || 0;
        quarterAdjustments[quarter.id].bizDeductions = currentDeductionsTarget - prevDeductionsTarget;
        return quarterAdjustments;
      },
      {
        q1: { bizIncome: 0, bizDeductions: 0 },
        q2: { bizIncome: 0, bizDeductions: 0 },
        q3: { bizIncome: 0, bizDeductions: 0 },
        q4: { bizIncome: 0, bizDeductions: 0 },
      },
    );
    return quarterAdjustments;
  },
);

export const getPotentialTaxTxnIds = createSelector(
  [
    getTaxPlanById,
    transactionsSelectors.getTransactionItemsById,
    dateSelectors.getCurrentDate,
    categoriesSelectors.getFederalEstimatedTaxPaymentCategories,
  ],
  (taxPlan, transactionsById, currentDate, taxCategories) => {
    const dueDates = taxDueDates(taxPlan?.input?.year || currentDate.year);
    return (
      transactionsById?.reduce((acc: string[], txn: TransactionItem) => {
        const { item } = txn;

        const shouldInclude =
          item.postedOn >= dueDates.q0Due &&
          item.postedOn <= dueDates.q4Due &&
          item.amount < 0 &&
          !transactionsUtils.isSplitTxn(txn.transaction) &&
          !transactionsUtils.isInvoiceTxn(txn.transaction) &&
          !transactionsUtils.isUITransferTxn(txn.transaction) &&
          !transactionsUtils.isPendingTxn(txn.transaction) &&
          !transactionsUtils.isUpcoming(txn.transaction) &&
          item.subtype !== 'INVOICE_PAYMENT' &&
          !taxCategories.find((category) => category.id === item.coa?.id);

        if (shouldInclude && txn.item.id) {
          acc.push(txn.item.id);
        }

        return acc;
      }, []) || []
    );
  },
);
